import { useState, useEffect } from 'react';
import fetchWithToken, { getUserData } from '../../services';

const PageCustomizationTab = ({ linkId }) => {
    const baseUrl = process.env.REACT_APP_BASE_URL;

    // Page customization state
    const [backgroundColor, setBackgroundColor] = useState('#68C7CC');
    const [textColor, setTextColor] = useState('#FFFFFF');
    const [showProfile, setShowProfile] = useState(true);
    const [customText, setCustomText] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isInitializing, setIsInitializing] = useState(true); // Track initialization state
    const [refreshKey, setRefreshKey] = useState(0); // For forcing iframe refresh

    console.log('page customizations before: ', backgroundColor, textColor, showProfile, customText);

    const colorPresets = [
      { name: 'Teal', value: '#68C7CC' },
      { name: 'Blue', value: '#007BFF' },
      { name: 'Green', value: '#28A745' },
      { name: 'Red', value: '#DC3545' },
      { name: 'Orange', value: '#FD7E14' },
      { name: 'Purple', value: '#6F42C1' },
      { name: 'Pink', value: '#E83E8C' },
      { name: 'Yellow', value: '#FFC107' },
      { name: 'Indigo', value: '#6610F2' },
      { name: 'Slate', value: '#6C757D' },
    ];

    // Save customization settings
    const saveCustomizations = async () => {
      if (!linkId) {
        console.error('No link available for saving page customizations');
        return;
      }

      setIsSaving(true);
      try {
        const response = await fetchWithToken('/update-page-customizations', {
          method: 'POST',
          body: JSON.stringify({
            backgroundColor,
            textColor,
            showProfile,
            text: customText || null, // Only send if not empty
          })
        });

        if (!response.ok) {
          throw new Error(`Server responded with ${response.status}`);
        }

        const data = await response.json();
        console.log('Page customizations saved:', data);
        // Force iframe refresh
        setRefreshKey(prevKey => prevKey + 1);
        console.log('refresh key: ', refreshKey);
      } catch (error) {
        console.error('Error saving page customizations:', error);
      } finally {
        setIsSaving(false);
      }
    };

    useEffect(() => {
      const fetchUserCustomizations = async () => {
        try {
          setIsLoading(true);
          setIsInitializing(true); // Set initializing flag before fetching
          const userData = await getUserData(['pageCustomizations']);

          if (userData.pageCustomizations) {
            const customizations = userData.pageCustomizations;

            if (customizations.backgroundColor) {
              setBackgroundColor(customizations.backgroundColor);
            }

            if (customizations.textColor) {
              setTextColor(customizations.textColor);
            }

            if (customizations.showProfile !== undefined) {
              setShowProfile(customizations.showProfile);
            }

            if (customizations.text) {
              setCustomText(customizations.text);
            }

            console.log('fetched page customizations');
          }
        } catch (error) {
          console.error('Error fetching user page customizations:', error);
        } finally {
          setIsLoading(false);
          // Wait for next tick to ensure all state updates have been processed
          setTimeout(() => setIsInitializing(false), 0);
        }
      };

      fetchUserCustomizations();
    }, []);

    // Effect to save customizations when options change (with debounce)
    useEffect(() => {
      // Only save if not loading and not initializing (prevents saving after initial fetch)
      if (!isLoading && !isInitializing) {
        // Add debounce to prevent too many requests
        const timer = setTimeout(() => {
          saveCustomizations();
          console.log('page customizations after: ', backgroundColor, textColor, showProfile, customText);
        }, 1000);

        return () => clearTimeout(timer);
      }
    }, [backgroundColor, textColor, showProfile, customText]);

    return (
      <div className="md:p-6">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Customization options - appears above on mobile, left column on desktop */}
          <div className="w-full md:w-1/4">
            <div className="bg-white p-2 md:py-6 rounded-md space-y-6">

              {/* Profile image toggle */}
              <div className="max-md:mb-8">
                <div className="flex items-center justify-between">
                  <label htmlFor="show-profile-toggle" className="font-medium text-black mr-4">Show profile image:</label>
                  <label className="inline-flex items-center cursor-pointer mr-1 md:mr-[10px]">
                    <input
                      id="show-profile-toggle"
                      type="checkbox"
                      checked={showProfile}
                      onChange={() => setShowProfile(!showProfile)}
                      className="sr-only peer"
                    />
                    <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-500"></div>
                  </label>
                </div>
              </div>

              {/* Custom text input */}
              <div className="max-md:flex max-md:flex-row max-md:items-center max-md:justify-between">
                <h4 id="custom-text-label" className="font-medium mb-2 max-md:mb-0 max-md:self-center mr-4">Text:</h4>
                <div role="radiogroup" aria-labelledby="custom-text-label" className="flex flex-wrap gap-2 max-md:mr-1">
                  {['Support', 'Tip', 'Donate'].map(text => (
                    <button
                      key={text}
                      role="radio"
                      aria-checked={customText === text}
                      onClick={() => setCustomText(text)}
                      className={`px-3 py-2 text-sm ${customText === text ? 'bg-[#68C7CC] text-white' : 'bg-gray-100 hover:bg-gray-200'} rounded-md transition-colors`}
                    >
                      {text}
                    </button>
                  ))}
                </div>
              </div>

              {/* Background color picker */}
              <div className="relative max-md:pt-4">
                <h4 id="bg-color-label" className="font-medium mb-2">Background color:</h4>
                <div role="radiogroup" aria-labelledby="bg-color-label" className="flex flex-wrap gap-2 mb-2">
                  {colorPresets.map((color) => (
                    <button
                      key={color.name}
                      role="radio"
                      aria-checked={backgroundColor === color.value}
                      aria-label={`Select ${color.name} as background color`}
                      onClick={() => setBackgroundColor(color.value)}
                      className="w-8 h-8 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: color.value }}
                      title={color.name}
                    >
                      {backgroundColor === color.value && (
                        <span className="text-white" aria-hidden="true">✓</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Preview pane - below on mobile, right side on desktop (3/4 width) */}
          <div className="w-full md:w-3/4">
            <div className="bg-white rounded-lg p-2 md:p-4">
              <h3 className="md:hidden font-medium text-black mb-4">Your page:</h3>
              <div className="rounded-md overflow-hidden h-[580px]">
                {!isLoading && linkId && (
                  <iframe
                    src={`${baseUrl}/${linkId}`}
                    title="Tipping Page Preview"
                    className="w-full h-full"
                    key={refreshKey}
                  />
                )}
                {isLoading && (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#68C7CC]"></div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  export default PageCustomizationTab;